'use client';

import { ReactNode, useState, useRef, useEffect, useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import { useClickOutside } from '@app/lib/click-outside/useClickOutside';

interface Props {
    content: ReactNode;
    children: ReactNode;
    className?: string;
    popoverClassName?: string;
}

export function InformationPopover({ content, children, className, popoverClassName }: Props) {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState<{
        top?: number;
        bottom?: number;
        left?: number;
        right?: number;
        transform?: string;
    }>({});
    const hoverTimeoutRef = useRef<NodeJS.Timeout>();
    const hideTimeoutRef = useRef<NodeJS.Timeout>();
    const triggerRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const handleClose = useCallback(() => {
        if (!isVisible) return;
        setIsVisible(false);
    }, [isVisible]);

    useClickOutside(handleClose, [triggerRef, popoverRef]);

    const calculatePosition = useCallback(() => {
        if (!triggerRef.current || !isVisible) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const margin = 16;

        const newPosition: { top?: number; bottom?: number; left?: number; right?: number; transform?: string } = {};

        const centerX = triggerRect.left + triggerRect.width / 2;
        const maxPopoverWidth = Math.min(512, viewportWidth - margin * 2);

        const leftIfCentered = centerX - maxPopoverWidth / 2;
        const rightIfCentered = centerX + maxPopoverWidth / 2;

        if (leftIfCentered >= margin && rightIfCentered <= viewportWidth - margin) {
            newPosition.left = centerX;
            newPosition.transform = 'translateX(-50%)';
        } else if (leftIfCentered < margin) {
            newPosition.left = margin;
        } else {
            newPosition.right = margin;
        }

        const spaceAbove = triggerRect.top;
        const spaceBelow = viewportHeight - triggerRect.bottom;
        const minRequiredSpace = 200 + margin;

        const shouldPositionAbove =
            spaceAbove >= minRequiredSpace || (spaceBelow < minRequiredSpace && spaceAbove > spaceBelow);

        if (shouldPositionAbove) {
            newPosition.bottom = viewportHeight - triggerRect.top + 8;
        } else {
            newPosition.top = triggerRect.bottom + 8;
        }

        setPosition(newPosition);
    }, [isVisible]);

    const handleMouseEnter = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = undefined;
        }

        hoverTimeoutRef.current = setTimeout(() => {
            setIsVisible(true);
        }, 200);
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = undefined;
        }

        hideTimeoutRef.current = setTimeout(() => {
            setIsVisible(false);
        }, 100);
    };

    useEffect(() => {
        if (isVisible) {
            calculatePosition();

            const handleResize = () => calculatePosition();
            const handleScroll = () => calculatePosition();

            window.addEventListener('resize', handleResize);
            window.addEventListener('scroll', handleScroll, true);

            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('scroll', handleScroll, true);
            };
        }
    }, [isVisible, calculatePosition]);

    const positionStyles = {
        top: position.top ? `${position.top}px` : undefined,
        bottom: position.bottom ? `${position.bottom}px` : undefined,
        left: position.left ? `${position.left}px` : undefined,
        right: position.right ? `${position.right}px` : undefined,
        transform: position.transform || undefined,
    };

    return (
        <div className={twMerge('relative inline-block', className)}>
            <div
                ref={triggerRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleMouseEnter}
                onBlur={handleMouseLeave}
                tabIndex={0}
                role="button"
                aria-label="Show information"
                className="cursor-help">
                {children}
            </div>

            {isVisible && (
                <div
                    ref={popoverRef}
                    className={twMerge(
                        'fixed z-10 flex flex-col',
                        'min-w-64 max-w-[32rem] break-words',
                        'rounded-lg bg-white shadow',
                        'p-4 text-sm leading-relaxed text-neutral-700',
                        'transition-opacity duration-150 ease-in-out',
                        popoverClassName,
                    )}
                    style={positionStyles}
                    role="tooltip"
                    aria-live="polite"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}>
                    {content}
                </div>
            )}
        </div>
    );
}
